// © 2026 AttainIP Engineering
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { fetchDashboard, fetchServices } from '@/api/queries';
import { Card } from '@/components/Card';
import { StatusBadge, type ServiceStatus } from '@/components/StatusBadge';
import { TierBadge } from '@/components/TierBadge';
import { ServiceDetail } from '@/components/ServiceDetail';
import { useAdminPrefs } from '@/lib/adminPrefs';

// Per-service endpoint URLs. Relative paths work behind ingress; absolute
// URLs are used for local dev (Vite proxy only covers admin BFF traffic).
//
// MCP services run a Starlette root (SSE transport) with a FastAPI admin
// sub-app mounted at /admin, so their admin endpoints live under that
// prefix — Swagger is at /admin/docs and /admin/env is /admin/env on the
// sub-app (the lib-core router contributes its own ``/admin`` prefix).
//
// We expose two URLs per service:
//   - ``docs`` — Swagger UI, surfaced only in alpha/beta (zeta has docs
//     stripped categorically per RFC-0010 §5).
//   - ``env`` — RFC-0010 identity probe, safe in every tier. This is the
//     production-friendly link.
// Service names must match the BFF's /admin/v1/services keys, which in turn
// match the Prometheus job labels (strip ``attain-`` prefix). The
// ``api-*`` ordering (api-identity, api-audit, …) follows the Prometheus
// convention rather than the older legacy aliases.
const isLocal = window.location.hostname === 'localhost';
const SERVICE_DOCS: Record<string, string> = isLocal ? {
  'api-identity': 'http://localhost:8121/docs',
  'ss-api': 'http://localhost:8201/docs',
  'ss-api-admin': 'http://localhost:8211/docs',
  'api-audit': 'http://localhost:8122/docs',
  'api-feedback': 'http://localhost:8124/docs',
  'api-tracking': 'http://localhost:8123/docs',
  'ss-mcp': 'http://localhost:8402/admin/docs',
  'agent-assistant': 'http://localhost:8401/docs',
  'agent-tools-mcp': 'http://localhost:8404/admin/docs',
} : {
  // Zeta: only externally-exposed services have an /app/* ingress —
  // Azure Front Door routes /app/* to AKS, then nginx routes per
  // service prefix. Internal-only services (audit, feedback, tracking,
  // agent-tools-mcp) are intentionally absent from the public lane,
  // so we omit them here; the dashboard renders "—" and operators
  // read the env data straight from the expand-row detail panel
  // (already populated by the BFF fan-out).
  'api-identity': '/app/api-identity/docs',
  'ss-api': '/app/api-ss/docs',
  'ss-api-admin': '/app/api-admin/docs',
  'ss-mcp': '/app/mcp-ss/admin/docs',
  'agent-assistant': '/app/api-agent/docs',
};
const SERVICE_ENV_URLS: Record<string, string> = isLocal ? {
  'api-identity': 'http://localhost:8121/admin/env',
  'ss-api': 'http://localhost:8201/admin/env',
  'ss-api-admin': 'http://localhost:8211/admin/env',
  'api-audit': 'http://localhost:8122/admin/env',
  'api-feedback': 'http://localhost:8124/admin/env',
  'api-tracking': 'http://localhost:8123/admin/env',
  'ss-mcp': 'http://localhost:8402/admin/env',
  'agent-assistant': 'http://localhost:8401/admin/env',
  'agent-tools-mcp': 'http://localhost:8404/admin/env',
} : {
  // Internal-only services (audit, feedback, tracking, agent-tools-mcp)
  // omitted — see SERVICE_DOCS comment above.
  'api-identity': '/app/api-identity/admin/env',
  'ss-api': '/app/api-ss/admin/env',
  'ss-api-admin': '/app/api-admin/admin/env',
  'ss-mcp': '/app/mcp-ss/admin/env',
  'agent-assistant': '/app/api-agent/admin/env',
};
// SPA env.json URLs — RFC-0010 SPA contract is a static file at SPA root.
const SPA_ENV_URLS: Record<string, string> = isLocal ? {
  'ss-ui-admin': 'http://localhost:8212/admin/env.json',
  'ss-ui': 'http://localhost:8202/env.json',
  'ss-ui-public': 'http://localhost:8203/env.json',
} : {
  'ss-ui-admin': '/app/devops/admin/env.json',
  'ss-ui': '/app/env.json',
  'ss-ui-public': '/env.json',
};

function StatCard({ title, total, last24h, last7d }: {
  title: string; total: number | string; last24h?: number | string; last7d?: number | string;
}) {
  return (
    <Card title={title}>
      <p className="text-3xl font-bold">{total}</p>
      <div className="flex gap-4 mt-2">
        {last24h != null && (
          <div>
            <span className="text-lg font-semibold text-blue-600">+{last24h}</span>
            <span className="text-xs text-gray-400 ml-1">24h</span>
          </div>
        )}
        {last7d != null && (
          <div>
            <span className="text-lg font-semibold text-indigo-600">+{last7d}</span>
            <span className="text-xs text-gray-400 ml-1">7d</span>
          </div>
        )}
      </div>
    </Card>
  );
}

type ServiceKind = 'api' | 'mcp' | 'agent' | 'spa' | 'edge';
type ServiceGroup = 'core' | 'ss' | 'agent' | 'other';

type ServiceRow = {
  name: string;
  kind: ServiceKind;
  group?: ServiceGroup;
  status: ServiceStatus;
  metric: string;
  target?: string;
  /** Multi-target blackbox probes (e.g., attain-edge-api covers four
   *  /admin/health URLs through the AFD lane). Rendered as multiple
   *  links in the Endpoint column. */
  targets?: string[];
};

// Mirrors the Python classifier in attain-ss-api-admin's prometheus_client.py.
// Used by the fan-out fallback path (no Prometheus, no ``group`` field)
// to derive consistent classification client-side.
function classifyServiceName(name: string, defaultKind: ServiceKind): { group: ServiceGroup; kind: ServiceKind } {
  let group: ServiceGroup = 'other';
  if (name.startsWith('api-')) group = 'core';
  else if (name.startsWith('ss-')) group = 'ss';
  else if (name.startsWith('agent-')) group = 'agent';

  let kind: ServiceKind = defaultKind;
  if (name.endsWith('-mcp')) kind = 'mcp';
  else if (name === 'agent-assistant') kind = 'agent';
  return { group, kind };
}

export function DashboardPage() {
  const { hideInternal } = useAdminPrefs();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', { hideInternal }],
    queryFn: () => fetchDashboard({ hide_internal: hideInternal }),
    refetchInterval: 30_000,
  });

  // /admin/v1/services still drives the version/schema columns (per-service
  // /admin/health fan-out). Status comes from Prometheus via /dashboard.
  const { data: servicesData } = useQuery({
    queryKey: ['services-detail'],
    queryFn: fetchServices,
    refetchInterval: 60_000,
  });

  if (isLoading) return <p className="text-gray-500">Loading dashboard...</p>;
  if (error) return <p className="text-red-600">Failed to load dashboard</p>;

  const stats = data?.stats || {};
  const users = stats.users || {};
  const analyses = stats.analyses || {};
  const payments = stats.payments || {};
  const revenue = stats.revenue || {};
  const byStatus = analyses.by_status || {};

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Users" total={users.total ?? '—'} last24h={users.last_24h} last7d={users.last_7d} />
        <StatCard title="Analyses" total={analyses.total ?? '—'} last24h={analyses.last_24h} last7d={analyses.last_7d} />
        <StatCard title="Transactions" total={payments.total ?? '—'} last24h={payments.last_24h} last7d={payments.last_7d} />
        <Card title="Revenue">
          <p className="text-3xl font-bold">
            {revenue.total_cents != null ? `$${(revenue.total_cents / 100).toFixed(2)}` : '—'}
          </p>
          <div className="flex gap-4 mt-2">
            <div>
              <span className="text-lg font-semibold text-green-600">
                {revenue.last_7d_cents != null ? `+$${(revenue.last_7d_cents / 100).toFixed(2)}` : '—'}
              </span>
              <span className="text-xs text-gray-400 ml-1">7d</span>
            </div>
            <div>
              <span className="text-sm text-gray-500">{revenue.purchases ?? 0} purchases</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Analysis status breakdown */}
      {Object.keys(byStatus).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Object.entries(byStatus).sort(([, a], [, b]) => (b as number) - (a as number)).map(([status, count]) => (
            <div key={status} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-center">
              <span className={`text-xs px-2 py-0.5 rounded ${
                status === 'complete' ? 'bg-green-100 text-green-700' :
                status === 'error' ? 'bg-red-100 text-red-700' :
                status === 'processing' ? 'bg-blue-100 text-blue-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>{status}</span>
              <p className="text-lg font-bold mt-1">{count as number}</p>
            </div>
          ))}
        </div>
      )}

      <Card title="Service Health">
        {data?.services_error && (
          <p className="text-xs text-orange-600 mb-3">
            Prometheus unavailable — falling back to direct /admin/health fan-out
            (status reflects reachability only): {data.services_error}
          </p>
        )}
        {(() => {
          // Prefer Prometheus-driven rows (covers APIs + SPAs/edges). If Prometheus
          // is unreachable (typical for local dev) fall back to synthesising rows
          // from the /admin/v1/services fan-out — APIs only, but enough to surface
          // tier/version/schema/env for RFC-0010 verification.
          const promServices = Array.isArray(data?.services) ? (data!.services as ServiceRow[]) : [];
          // Fan-out fallback for local dev (no Prometheus). The kind
          // can't come from Prometheus job labels here, so we use a name
          // heuristic — anything starting with ``ss-ui`` or ``ss-public``
          // is a SPA, everything else is treated as an API. The Prometheus
          // path classifies more precisely when available.
          const fallbackServices: ServiceRow[] = promServices.length === 0 && servicesData?.services
            ? Object.entries(servicesData.services as Record<string, { healthy?: boolean }>).map(
                ([name, detail]) => {
                  const defaultKind: ServiceKind = /^(ss-ui|ss-public)/.test(name) ? 'spa' : 'api';
                  const { group, kind } = classifyServiceName(name, defaultKind);
                  return {
                    name,
                    group,
                    kind,
                    status: (detail?.healthy ? 'up' : 'down') as ServiceStatus,
                    metric: 'fan-out',
                  };
                },
              )
            : [];
          const effective = promServices.length > 0 ? promServices : fallbackServices;
          if (effective.length === 0) {
            return <p className="text-gray-500 text-sm">No service data</p>;
          }
          return (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 font-medium w-6"></th>
                <th className="pb-2 font-medium">Group</th>
                <th className="pb-2 font-medium">Service</th>
                <th className="pb-2 font-medium">Kind</th>
                <th className="pb-2 font-medium">Tier</th>
                <th className="pb-2 font-medium">Version</th>
                <th className="pb-2 font-medium">DB Schema</th>
                <th className="pb-2 font-medium">Endpoint</th>
                <th className="pb-2 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {[...effective]
                .sort((a, b) => {
                  // Alphabetical by group, then alphabetical by name within group.
                  const ga = a.group ?? 'other';
                  const gb = b.group ?? 'other';
                  if (ga !== gb) return ga.localeCompare(gb);
                  return a.name.localeCompare(b.name);
                })
                .map((svc) => {
                  // For APIs, look up version/schema/docs/env from the /services payload.
                  // env comes from each service's /admin/env (RFC-0010) and is best-effort:
                  // services that haven't adopted the endpoint yet won't have a tier.
                  const detail = servicesData?.services?.[svc.name];
                  const env = detail?.env;
                  // In zeta link to /admin/env (RFC-0010 identity probe, safe
                  // in prod). In alpha/beta link to Swagger /docs for ad-hoc
                  // testing. When tier is unknown (env not adopted yet) default
                  // to /docs — most likely a non-prod environment.
                  const tier = env?.tier;
                  const endpointUrl = tier === 'zeta' ? SERVICE_ENV_URLS[svc.name] : SERVICE_DOCS[svc.name];
                  const endpointLabel = tier === 'zeta' ? '/admin/env' : '/docs';
                  // Prefer env.service_version (RFC-0010 canonical) over the /admin/health field.
                  // Only prefix ``v`` when the value looks like a semver (starts with a digit);
                  // dev fallbacks like ``"dev"`` or ``"unknown"`` are surfaced as-is.
                  const raw = env?.service_version ?? detail?.version ?? null;
                  const version = raw ? (/^\d/.test(String(raw)) ? `v${raw}` : String(raw)) : '—';
                  const schema = detail?.db?.current_version || detail?.db?.version || '—';
                  const rowKey = `${svc.kind}/${svc.name}`;
                  const isOpen = !!expanded[rowKey];
                  const toggle = () => setExpanded((s) => ({ ...s, [rowKey]: !s[rowKey] }));
                  return (
                    <Fragment key={rowKey}>
                    <tr
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={toggle}
                    >
                      <td className="py-2 text-gray-400">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="py-2">
                        {svc.group ? (
                          <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                            svc.group === 'core' ? 'bg-slate-100 text-slate-700' :
                            svc.group === 'ss' ? 'bg-cyan-50 text-cyan-700' :
                            svc.group === 'agent' ? 'bg-fuchsia-50 text-fuchsia-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {svc.group}
                          </span>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="py-2 font-mono">{svc.name}</td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          svc.kind === 'api' ? 'bg-blue-50 text-blue-700' :
                          svc.kind === 'mcp' ? 'bg-emerald-50 text-emerald-700' :
                          svc.kind === 'agent' ? 'bg-violet-50 text-violet-700' :
                          svc.kind === 'spa' ? 'bg-purple-50 text-purple-700' :
                          'bg-amber-50 text-amber-700'
                        }`}>
                          {svc.kind}
                        </span>
                      </td>
                      <td className="py-2">
                        {env?.tier ? (
                          <TierBadge tier={env.tier} rawEnv={env.service_env_raw} />
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2 text-gray-500">{version}</td>
                      <td className="py-2 text-gray-500">{svc.kind === 'api' ? schema : '—'}</td>
                      <td className="py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                        {(svc.kind === 'api' || svc.kind === 'mcp' || svc.kind === 'agent') ? (
                          endpointUrl ? (
                            <a href={endpointUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {endpointLabel}
                            </a>
                          ) : '—'
                        ) : svc.kind === 'spa' ? (
                          // SPAs: Prometheus probe gives a homepage URL (svc.target);
                          // fallback path doesn't have one, so link env.json instead.
                          svc.target ? (
                            <a href={svc.target.startsWith('http') ? svc.target : undefined} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate inline-block max-w-xs">
                              {svc.target}
                            </a>
                          ) : SPA_ENV_URLS[svc.name] ? (
                            <a href={SPA_ENV_URLS[svc.name]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              /env.json
                            </a>
                          ) : '—'
                        ) : svc.targets && svc.targets.length > 0 ? (
                          // Multi-target blackbox probes (e.g., edge-api).
                          // Render one clickable label per URL — shows just
                          // the path component so the row stays compact.
                          <div className="flex flex-wrap gap-1.5">
                            {svc.targets.map((t) => {
                              let label = t;
                              try { label = new URL(t).pathname; } catch { /* leave full */ }
                              return (
                                <a
                                  key={t}
                                  href={t}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                  title={t}
                                >
                                  {label}
                                </a>
                              );
                            })}
                          </div>
                        ) : svc.target ? (
                          <a href={svc.target.startsWith('http') ? svc.target : undefined} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate inline-block max-w-xs">
                            {svc.target}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="py-2 text-right"><StatusBadge status={svc.status} /></td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b last:border-0 bg-gray-50">
                        <td></td>
                        <td colSpan={8} className="py-3 pr-4">
                          <ServiceDetail detail={detail} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
            </tbody>
          </table>
          );
        })()}
      </Card>
    </div>
  );
}
